import { createContext, useContext, useEffect, useState } from "react";

/**
 * The Run page's widths while a step puts side panes in its margins (Johannes, 4 Nov 09:34 and 11:11): the Run's
 * column gives up width down to its narrowest, so each margin gets at least its narrowest; in a wide pane the column
 * keeps its usual width (ThreadPage's 56rem) and the margins take up to their widest.
 */
const GAP_REM = 1;
const MARGIN_MIN_REM = 12;
const MARGIN_MAX_REM = 22;
const COLUMN_MIN_REM = 33;
const COLUMN_MAX_REM = 56;
/** The narrowest pane with room for both margins beside a narrowed column. */
const PANE_MIN_REM = COLUMN_MIN_REM + 2 * (MARGIN_MIN_REM + GAP_REM);

export const RUN_MARGINS_GAP = `${String(GAP_REM)}rem`;
/** The steps' grid while they have margins: margin, column, margin. */
export const RUN_MARGINS_COLUMNS = `minmax(${String(MARGIN_MIN_REM)}rem, ${String(MARGIN_MAX_REM)}rem) minmax(${String(COLUMN_MIN_REM)}rem, ${String(COLUMN_MAX_REM)}rem) minmax(${String(MARGIN_MIN_REM)}rem, ${String(MARGIN_MAX_REM)}rem)`;

/** Whether a pane `paneWidth` px wide has room for both margins beside the Run's column, at `rem` px to the rem. */
export function runMarginsFit(paneWidth: number, rem: number): boolean {
  return paneWidth >= PANE_MIN_REM * rem;
}

/**
 * The Run page's margins beside its column, where the step in view puts its side panes: a change set's review its
 * outline left and its context right. `null` while the pane is too narrow; the step then keeps its panes in the
 * column.
 */
export interface RunMargins {
  readonly left: HTMLElement;
  readonly right: HTMLElement;
}

export const RunMarginsContext = createContext<RunMargins | null>(null);
/** How a step asks the Run page for margins while it is shown: the call returns its release. */
export const RunMarginsRequestContext = createContext<(() => () => void) | null>(null);

/** The margins of the Run page this view is in, or `null`; asking for them, while mounted, makes the page give them room. */
export function useRunMargins(): RunMargins | null {
  const request = useContext(RunMarginsRequestContext);
  useEffect(() => request?.(), [request]);
  return useContext(RunMarginsContext);
}

/** Whether the pane `measure` spans has room for the margins, as its width changes. */
export function useRunMarginsRoom(measure: HTMLElement | null): boolean {
  const [room, setRoom] = useState(false);
  useEffect(() => {
    if (measure === null || typeof ResizeObserver === "undefined") return undefined;
    const update = () => {
      const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      setRoom(runMarginsFit(measure.clientWidth, rem));
    };
    const observer = new ResizeObserver(update);
    observer.observe(measure);
    update();
    return () => {
      observer.disconnect();
    };
  }, [measure]);
  return room;
}

/** An element's height in pixels, as it changes; 0 until it is there. */
export function useElementHeight(element: HTMLElement | null): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (element === null || typeof ResizeObserver === "undefined") return undefined;
    // The observer reports the element's first size when it starts observing, then each change.
    const observer = new ResizeObserver(() => { setHeight(element.offsetHeight); });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [element]);
  return height;
}

/** The nearest ancestor of `element` that scrolls vertically, which a sticky pane inside it stays in view in. */
export function scrollPaneOf(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement;
  while (node !== null) {
    const overflow = getComputedStyle(node).overflowY;
    if (overflow === "auto" || overflow === "scroll") return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * The height of the scroll pane `element` sits in, as it changes; `null` until it is known. A margin's pane is as tall
 * as that pane leaves above the message box: the window's height overstates it by the top bar and the tabs (Sol R13).
 */
export function useScrollPaneHeight(element: HTMLElement | null): number | null {
  const [height, setHeight] = useState<number | null>(null);
  useEffect(() => {
    const pane = element === null ? null : scrollPaneOf(element);
    if (pane === null || typeof ResizeObserver === "undefined") return undefined;
    // The observer reports the pane's first size when it starts observing, then each change.
    const observer = new ResizeObserver(() => { setHeight(pane.clientHeight); });
    observer.observe(pane);
    return () => {
      observer.disconnect();
    };
  }, [element]);
  return height;
}
